import React, { useEffect, useRef, useState } from 'react';
import api from '../../config/axiosConfig';
import { sessionManager } from '../../config/sessionManager';
import { showErrorMsg, showSuccessMsg } from '../../config/common';

export interface KPIGroupTargets {
  possession: {
    possessionPercent: number;
    groundBallsPP: number;
    maxTurnoversPP: number;
  };
  offense: {
    minShotsPP: number;
    minGoalsPP: number;
    shotPercent: number;
    emosPercent: number;
  };
  defense: { maxShotsAPP: number; savePercent: number; mddsPercent: number };
  clearsRides: { clearPercent: number; minRidesPP: number };
  faceoffs: { faceoffPercent: number };
}

const DEFAULT_TARGETS: KPIGroupTargets = {
  possession: { possessionPercent: 0, groundBallsPP: 0, maxTurnoversPP: 0 },
  offense: { minShotsPP: 0, minGoalsPP: 0, shotPercent: 0, emosPercent: 0 },
  defense: { maxShotsAPP: 0, savePercent: 0, mddsPercent: 0 },
  clearsRides: { clearPercent: 0, minRidesPP: 0 },
  faceoffs: { faceoffPercent: 0 },
};

export type KPIScope = 'season' | 'game';

// Hoisted components to prevent remount/focus loss on parent re-renders
const Section: React.FC<{ label: string }> = ({ label, children }) => (
  <div className="shadow-lg">
    <div className="bg-sd-navy text-white text-lg font-semibold px-4 py-2">{label}</div>
    <div className="bg-white border border-t-0 border-sd-line px-3 py-0 divide-y divide-[#e3f3ec]">{children}</div>
  </div>
);

const NumberRow: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  disabled?: boolean;
}> = ({ label, value, onChange, min = 0, max, disabled = false }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const isFocusedRef = useRef<boolean>(false);
  const handleFocus = () => {
    isFocusedRef.current = true;
  };

  const [text, setText] = useState<string>(() => String(value ?? 0));

  useEffect(() => {
    if (!isFocusedRef.current) {
      setText(String(value ?? 0));
    }
  }, [value]);

  const clamp = (n: number) => {
    if (max !== undefined && n > max) return max;
    if (min !== undefined && n < min) return min;
    return n;
  };

  const commit = (raw: string) => {
    const cleaned = raw.trim();
    const num = cleaned === '' || cleaned === '.' || cleaned === '-' ? NaN : Number(cleaned);
    if (!isNaN(num)) {
      const clamped = clamp(num);
      setText(String(clamped));
      onChange(clamped);
    } else {
      setText(String(value ?? 0));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    setText(next);
    const num = Number(next);
    if (!isNaN(num)) {
      onChange(clamp(num));
    }
  };

  const stepBy = (delta: number) => {
    const cur = Number(text);
    const base = isNaN(cur) ? Number(value ?? 0) : cur;
    const next = clamp((base || 0) + delta);
    setText(String(next));
    onChange(next);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      stepBy(1);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      stepBy(-1);
    } else if (e.key === 'Enter') {
      commit(text);
    }
  };

  const handleBlur = () => {
    isFocusedRef.current = false;
    commit(text);
  };

  return (
    <div className="flex items-center justify-between py-2">
      <label className="text-gray-700 font-medium text-base md:text-lg">{label}</label>
      <div className="flex items-center gap-1 md:gap-2">
        <button
          type="button"
          aria-label="Decrement"
          onClick={() => stepBy(-1)}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          disabled={disabled}
          className="h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded border border-[#d6f5e6] bg-white text-sd-navy hover:bg-[#f7f7f7] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          −
        </button>
        <input
          ref={inputRef}
          type="text"
          inputMode={max !== undefined && max <= 100 ? 'decimal' : 'numeric'}
          pattern={max !== undefined && max <= 100 ? '[0-9]*\\.?[0-9]*' : '[0-9]*'}
          value={text}
          onFocus={handleFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disabled}
          className="w-16 md:w-20 text-center px-2 py-1 md:py-1.5 border border-[#d6f5e6] rounded bg-[#f3fcf7] font-bold text-base md:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={label}
          min={min}
          {...(max !== undefined ? { max } : {})}
        />
        <button
          type="button"
          aria-label="Increment"
          onClick={() => stepBy(1)}
          onMouseDown={(e) => e.preventDefault()}
          onTouchStart={(e) => e.preventDefault()}
          disabled={disabled}
          className="h-8 w-8 md:h-9 md:w-9 flex items-center justify-center rounded border border-[#d6f5e6] bg-white text-sd-navy hover:bg-[#f7f7f7] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          +
        </button>
      </div>
    </div>
  );
};

interface KPISettingsProps {
  scope: KPIScope;
  fetchUrl: string; // endpoint without query string
  updateUrl: string;
  title?: string;
  description?: string;
  includeGameIdParam?: boolean; // default true for game scope
}

const KPISettings: React.FC<KPISettingsProps> = ({
  scope,
  fetchUrl,
  updateUrl,
  title,
  description,
  includeGameIdParam = true,
}) => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  // Check KPI edit permission
  const canEditKPIs = sessionManager.canPerformAction('games.gameMenu.kpis', 'edit');

  const [targets, setTargets] = useState<KPIGroupTargets>(DEFAULT_TARGETS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showLegend, setShowLegend] = useState(false);

  // Build query params
  const buildQuery = () => {
    const params: Record<string, string> = {};
    if (clientId) params.clientId = clientId;
    if (seasonId) params.seasonId = seasonId;
    if (includeGameIdParam && scope === 'game' && gameId && gameId !== 'all') params.gameId = gameId;
    const qs = new URLSearchParams(params).toString();
    return `${fetchUrl}?${qs}`;
  };

  useEffect(() => {
    const fetchTargets = async () => {
      if (!clientId || !seasonId) return;
      setLoading(true);
      try {
        const url = buildQuery();
        const res = await api.get(url);
        if (res.data.success) {
          setTargets({ ...DEFAULT_TARGETS, ...res.data.data });
        }
      } catch (e) {
        console.error('Failed to fetch KPI targets', e);
      } finally {
        setLoading(false);
      }
    };
    fetchTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, seasonId, gameId, scope]);

  const getVal = (path: string) => {
    return path.split('.').reduce<any>((acc, k) => acc?.[k], targets) ?? 0;
  };

  const setVal = (path: string, value: number) => {
    setTargets((prev) => {
      const clone: any = JSON.parse(JSON.stringify(prev));
      const keys = path.split('.');
      let cur = clone;
      for (let i = 0; i < keys.length - 1; i++) cur = cur[keys[i]];
      cur[keys[keys.length - 1]] = value;
      return clone;
    });
  };

  // Helper to bind path-based state updates to NumberRow onChange
  const pathSetter = (path: string) => (val: number) => setVal(path, val);

  const save = async () => {
    if (!clientId || !seasonId) return;
    setSaving(true);
    try {
      const payload: any = { clientId, seasonId, targets };
      if (includeGameIdParam && scope === 'game') payload.gameId = gameId;
      await api.post(updateUrl, payload);
      showSuccessMsg('KPI targets updated successfully');
    } catch (e: any) {
      showErrorMsg(e.response?.data?.message || e.message || 'Failed to update KPI targets');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="w-full px-4 py-6 sm:px-6">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-sd-navy">
          {title || (scope === 'game' ? 'Game KPI Targets' : 'Season KPI Targets')}
        </h1>
        <p className="mt-2 text-sm sm:text-base text-sd-navy font-medium break-all">
          {description ||
            (scope === 'game'
              ? 'Override season-level KPI targets for this specific game when opponent strength or conditions differ.'
              : 'Set organization-wide default KPI targets for the current season; games can override as needed.')}
        </p>
      </div>
      {loading && <div className="px-6 py-4">Loading...</div>}
      {!loading && (
        <div className="flex flex-col pb-4 gap-6">
          <div className="flex justify-end">
            <button
              onClick={() => setShowLegend(true)}
              className="bg-sd-navy text-white text-sm px-3 py-2 rounded hover:bg-[#043661] transition-colors"
            >
              KPI Color Legend
            </button>
          </div>

          <Section label="Possession">
            <NumberRow
              label="Possession %"
              value={getVal('possession.possessionPercent')}
              onChange={pathSetter('possession.possessionPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Ground Balls %"
              value={getVal('possession.groundBallsPP')}
              onChange={pathSetter('possession.groundBallsPP')}
              max={100}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Max Turnovers per Quarter"
              value={getVal('possession.maxTurnoversPP')}
              onChange={pathSetter('possession.maxTurnoversPP')}
              disabled={!canEditKPIs}
            />
          </Section>

          <Section label="Offense">
            <NumberRow
              label="Min Shots per Quarter"
              value={getVal('offense.minShotsPP')}
              onChange={pathSetter('offense.minShotsPP')}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Min Goals per Quarter"
              value={getVal('offense.minGoalsPP')}
              onChange={pathSetter('offense.minGoalsPP')}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Shot %"
              value={getVal('offense.shotPercent')}
              onChange={pathSetter('offense.shotPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="MUOS %"
              value={getVal('offense.emosPercent')}
              onChange={pathSetter('offense.emosPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
          </Section>

          <Section label="Defense">
            <NumberRow
              label="Max Shots Allowed Per Quarter"
              value={getVal('defense.maxShotsAPP')}
              onChange={pathSetter('defense.maxShotsAPP')}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Save %"
              value={getVal('defense.savePercent')}
              onChange={pathSetter('defense.savePercent')}
              max={100}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="MDDS %"
              value={getVal('defense.mddsPercent')}
              onChange={pathSetter('defense.mddsPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
          </Section>

          <Section label="Clears / Rides">
            <NumberRow
              label="Clear %"
              value={getVal('clearsRides.clearPercent')}
              onChange={pathSetter('clearsRides.clearPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
            <NumberRow
              label="Min Rides Per Quarter"
              value={getVal('clearsRides.minRidesPP')}
              onChange={pathSetter('clearsRides.minRidesPP')}
              disabled={!canEditKPIs}
            />
          </Section>

          <Section label="Draw Controls">
            <NumberRow
              label="DC %"
              value={getVal('faceoffs.faceoffPercent')}
              onChange={pathSetter('faceoffs.faceoffPercent')}
              max={100}
              disabled={!canEditKPIs}
            />
          </Section>

          <div className="flex justify-center mt-2">
            <button
              onClick={save}
              disabled={saving || !canEditKPIs}
              className="bg-sd-accent text-black font-semibold py-2 px-8 rounded text-lg shadow-md transition-colors duration-200 hover:bg-[#6fd420] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Updates'}
            </button>
          </div>
        </div>
      )}

      {showLegend && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold mb-4">KPI Color Legend</h3>
            <div className="space-y-2">
              <div className="flex items-center">
                <div className="w-4 h-4 bg-green-500 rounded mr-3"></div>
                <span>Achieving or exceeding target (≥100%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-yellow-500 rounded mr-3"></div>
                <span>Close to target (85-99%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-orange-500 rounded mr-3"></div>
                <span>Below target (80-84%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-red-500 rounded mr-3"></div>
                <span>Significantly below target (&lt;80%)</span>
              </div>
              <div className="flex items-center">
                <div className="w-4 h-4 bg-gray-400 rounded mr-3"></div>
                <span>No target set</span>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowLegend(false)}
                className="mt-4 bg-sd-navy text-white px-4 py-2 rounded hover:bg-[#043661] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPISettings;
